import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { ApiError } from "../lib/api";
import { Brand } from "../components/Brand";
import { Constellation } from "../components/Constellation";
import { GoogleButton } from "../components/GoogleButton";

export default function Login() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signIn(email.trim(), password);
      const next = (loc.state as { from?: string } | null)?.from ?? "/library";
      nav(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr]">
      {/* LEFT - quiet brand panel */}
      <section className="hidden lg:flex flex-col justify-center bg-paper-deep border-r border-hairline-bold px-12 xl:px-16 py-16">
        <Link to="/" aria-label="DreamBook home" className="self-start mb-10">
          <Brand size="lg" />
        </Link>
        <p className="eyebrow">An engine for full-length books</p>
        <h1 className="mt-3 font-serif text-[clamp(1.9rem,3vw,2.8rem)] leading-[1.08] text-ink-body">
          Draft, critique, and <em className="italic text-terracotta">revise</em> a whole book, then finish it in the editor.
        </h1>
        <p className="mt-4 max-w-[46ch] font-body text-[17px] leading-relaxed text-ink-dim">
          One engine. Hand it your idea and it drafts, critiques, and revises the whole book — then sends you the finished manuscript.
        </p>
        <p className="mt-8 font-body text-[17px] text-ink-body">
          <span className="font-medium">Start free. 2,000 credits, no card.</span>
        </p>
        <p className="mt-10 font-hand text-[1.25rem] text-ink-faint">Dreambook into being.</p>
      </section>

      {/* RIGHT - form card */}
      <section className="flex flex-col justify-center items-center px-6 sm:px-10 py-14">
        <div className="w-full max-w-[440px]">
          {/* compact brand for narrow viewports */}
          <Link to="/" aria-label="DreamBook home" className="lg:hidden mx-auto mb-10 block w-fit">
            <Brand size="md" />
          </Link>

          <div className="eyebrow">Sign in</div>
          <h2 className="mt-2 font-serif text-[clamp(1.5rem,2.4vw,2rem)] leading-tight text-ink-body">
            Welcome back
          </h2>
          <p className="mt-2 font-body text-[15px] text-ink-dim">
            Sign in to return to your books and credits.
          </p>

          <div className="surface mt-7 p-7 sm:p-8">
            <form onSubmit={onSubmit}>
              <label className="field-label" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="field mb-5"
                placeholder="you@example.com"
              />

              <label className="field-label" htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="field mb-6"
                placeholder="••••••••"
              />

              {error ? (
                <div className="mb-5 text-sm text-wax" role="alert">{error}</div>
              ) : null}

              <button type="submit" className="btn-primary w-full" disabled={loading}>
                {loading ? "Signing in…" : "Sign in"}
              </button>

              <GoogleButton label="signin_with" />
            </form>

            <p className="mt-6 text-center font-body text-sm text-ink-dim">
              New here?{" "}
              <Link to="/register" className="text-terracotta hover:text-gold-300">
                Create an account
              </Link>
            </p>
          </div>

          <div className="mt-10 flex justify-center opacity-50">
            <Constellation />
          </div>
        </div>
      </section>
    </div>
  );
}
